import { useEffect, useRef } from 'react';
import { useRouter } from '@tanstack/react-router';
import { queryClient } from '@/App.tsx';
import { bookingApiQueryKeys } from '@/features/public/booking/api/bookingApi.queryKeys.ts';
import { isRegistrationUnavailableError } from '@/features/public/booking/api/bookingErrors.ts';

/**
 * Reaguje na odmowę z powodu okna zapisów, stanu wyjazdu albo braku miejsc
 * (`TOUR_REGISTRATION_*`, `TOUR_CANCELLED`, `TOUR_FULL`) zgłoszoną przez wycenę.
 *
 * Wycena to zapytanie, nie mutacja — nie ma tu `onError`, w którym dałoby się to
 * obsłużyć, a klient siedzący na formularzu w chwili zamknięcia zapisów
 * zobaczyłby wyłącznie komunikat pod ceną. Świeże `basicInfo` przełącza route na
 * właściwy ekran (`registration-closed`, `tour-cancelled`, `sold-out`).
 *
 * Odpalamy **raz na cykl życia komponentu**: gdyby backend odmawiał mimo
 * odświeżonych danych wyjazdu, ponawianie wpadłoby w pętlę
 * przeładowanie → refetch → ten sam błąd.
 */
export const useRegistrationAvailabilityGuard = (
  error: Error | null,
  orgSlug: string,
  tourSlug: string,
) => {
  const router = useRouter();
  const alreadyReactedRef = useRef(false);

  useEffect(() => {
    if (alreadyReactedRef.current) return;
    if (!error || !isRegistrationUnavailableError(error)) return;

    alreadyReactedRef.current = true;
    void queryClient
      .invalidateQueries({
        queryKey: bookingApiQueryKeys.basicInfo(orgSlug, tourSlug),
      })
      .then(() => router.invalidate());
  }, [error, orgSlug, tourSlug, router]);
};

export default useRegistrationAvailabilityGuard;
